import CollectApi from '@/api/Collect'
import type {
  CollectAuditFetchParams,
  CollectFetchData,
} from '@/api/Collect/types.ts'
import type {
  FetchParams,
  LayoutType,
  Paginated,
  ParamType,
} from '@/api/types.ts'
import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query'

export function useFetchCollects(
  initiativeId: ParamType,
  featureId: ParamType,
  params: CollectAuditFetchParams,
  options?: Omit<
    UseQueryOptions<unknown, unknown, Paginated<CollectFetchData>, unknown[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    ...options,
    queryKey: ['fetch-collect', initiativeId, featureId, params],
    queryFn: () => CollectApi.fetch([initiativeId, featureId, 'web'], params),
  })
}

export function getCollectQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType,
  layoutType: LayoutType,
  collectId: ParamType,
  version?: ParamType
) {
  return queryOptions({
    queryKey: [
      'get-collect',
      initiativeId,
      featureId,
      layoutType,
      collectId,
      version,
    ],
    queryFn: () =>
      CollectApi.get([initiativeId, featureId, layoutType, collectId], version),
  })
}

export function useGetCollect(
  initiativeId: ParamType,
  featureId: ParamType,
  layoutType: LayoutType,
  collectId: ParamType,
  version?: ParamType,
  options?: { enabled?: boolean }
) {
  return useQuery({
    ...getCollectQueryOptions(
      initiativeId,
      featureId,
      layoutType,
      collectId,
      version
    ),
    ...options,
  })
}

export function fetchCollectAuditsQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType,
  collectId: ParamType,
  params: FetchParams
) {
  return queryOptions({
    queryKey: [
      'fetch-collect-audits',
      initiativeId,
      featureId,
      collectId,
      params,
    ],
    queryFn: () =>
      CollectApi.fetchAudits(
        [initiativeId, featureId, 'web', collectId],
        params
      ),
  })
}

export function useFetchCollectAudits(
  initiativeId: ParamType,
  featureId: ParamType,
  collectId: ParamType,
  params: FetchParams
) {
  return useQuery(
    fetchCollectAuditsQueryOptions(initiativeId, featureId, collectId, params)
  )
}

export function getDescendingRelationsQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType,
  collectId: ParamType
) {
  return queryOptions({
    queryKey: [
      'get-collect-relations-descending',
      initiativeId,
      featureId,
      collectId,
    ],
    queryFn: () =>
      CollectApi.getDescendingRelations([initiativeId, featureId, collectId]),
  })
}

export function useGetDescendingRelations(
  initiativeId: ParamType,
  featureId: ParamType,
  collectId: ParamType,
  options?: { enabled?: boolean }
) {
  return useQuery({
    ...getDescendingRelationsQueryOptions(initiativeId, featureId, collectId),
    ...options,
  })
}

export function getAscendingRelationsQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType,
  collectId: ParamType
) {
  return queryOptions({
    queryKey: [
      'get-collect-relations-ascending',
      initiativeId,
      featureId,
      collectId,
    ],
    queryFn: () =>
      CollectApi.getAscendingRelations([initiativeId, featureId, collectId]),
  })
}

export function useGetAscendingRelations(
  initiativeId: ParamType,
  featureId: ParamType,
  collectId: ParamType,
  options?: { enabled?: boolean }
) {
  return useQuery({
    ...getAscendingRelationsQueryOptions(initiativeId, featureId, collectId),
    ...options,
  })
}
